import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { CheckOutlined, FileCopyOutlined } from "@material-ui/icons";
// Usados pela funcionalidade de baixar a assinatura como arquivo HTML
// (downloadHtmlFile), hoje desativada na UI mas mantida para reativacao.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import DownloadIcon from "@mui/icons-material/Download";
import { Alert, Select, SelectChangeEvent } from "@mui/material";
import React from "react";
import ReactDOMServer from "react-dom/server";
import "./App.css";
import { mediaAssetUrl } from "./assets";
import { COMPANIES, COMPANY_ORDER } from "./companyConfig";
import { formatBrazilianPhone } from "./phoneFormat";
import Signature from "./Signature";
import { PhotoSignatureProps } from "./signatureTypes";
import { buildDownloadFileName } from "./signatureUtils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& .MuiTextField-root": {
        margin: theme.spacing(0),
      },
      "& .label-root": {
        margin: theme.spacing(0),
      },
    },
    paper: {
      padding: theme.spacing(2),
      textAlign: "left",
      color: theme.palette.text.secondary,
    },
    centeredText: {
      textAlign: "center",
      fontFamily: "'Instrument Sans', sans-serif",
      fontWeight: 700,
    },
    box: {
      width: "100%",
    },
    inputLabel: {
      marginLeft: 10,
      marginTop: 3,
    },
    select: {
      width: "100%",
      marginLeft: 0.7,
      "& .MuiSelect-select.MuiSelect-select": {
        boxSizing: "border-box",
        minHeight: "1.4375em",
        padding: "19.5px 9px 2px 9px !important",
      },
    },
    inlineSelectControl: {
      width: "calc(100% - 0px)",
      marginTop: theme.spacing(0),
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(0),
    },
    instructionsVideo: {
      display: "block",
      width: "100%",
      maxHeight: "70vh",
      backgroundColor: "#000000",
    },
  })
);

interface State extends PhotoSignatureProps {
  copyStatus: "idle" | "success" | "error";
}

type ClipboardWithWrite = {
  write?: (data: unknown[]) => Promise<void>;
};

const pronounOptions = ["ele/dele", "ela/dela", "elu/delu"] as const;

const initialSignatureState: PhotoSignatureProps = {
  companyKey: "om",
  fullName: "",
  pronoun: "",
  title: "",
  mobile: "",
};

const initialState: State = {
  ...initialSignatureState,
  copyStatus: "idle",
};

function App() {
  const classes = useStyles();
  const [state, setState] = React.useState<State>(initialState);
  const [isInstructionsOpen, setIsInstructionsOpen] = React.useState(false);

  const hasRequiredFields = !!state.companyKey && !!state.fullName && !!state.title;
  const hasFormChanges =
    state.companyKey !== initialSignatureState.companyKey ||
    state.fullName !== initialSignatureState.fullName ||
    state.pronoun !== initialSignatureState.pronoun ||
    state.title !== initialSignatureState.title ||
    state.mobile !== initialSignatureState.mobile;

  const signatureProps: PhotoSignatureProps = {
    companyKey: state.companyKey,
    fullName: state.fullName,
    pronoun: state.pronoun,
    title: state.title,
    mobile: state.mobile,
  };

  const renderSignatureHtml = () =>
    ReactDOMServer.renderToStaticMarkup(
      <Signature {...signatureProps} assetMode="production" />
    );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const nextValue = name === "mobile" ? formatBrazilianPhone(value) : value;

    setState((prevState) => ({
      ...prevState,
      [name]: nextValue,
      copyStatus: "idle",
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
      copyStatus: "idle",
    }));
  };

  const copyHtmlWithSelection = (htmlSignature: string) => {
    const container = document.createElement("div");
    const range = document.createRange();
    const selection = window.getSelection();

    if (!selection) {
      return false;
    }

    container.setAttribute("contenteditable", "true");
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.innerHTML = htmlSignature;

    document.body.appendChild(container);
    range.selectNodeContents(container);
    selection.removeAllRanges();
    selection.addRange(range);

    const copySucceeded = document.execCommand("copy");

    selection.removeAllRanges();
    container.parentNode?.removeChild(container);

    return copySucceeded;
  };

  const copyHtmlToClipboard = async (htmlSignature: string, textSignature: string) => {
    const clipboard = navigator.clipboard as ClipboardWithWrite | undefined;
    const clipboardItemConstructor = (
      globalThis as {
        ClipboardItem?: new (items: Record<string, Blob>) => unknown;
      }
    ).ClipboardItem;

    if (clipboard?.write && clipboardItemConstructor) {
      try {
        await clipboard.write([
          new clipboardItemConstructor({
            "text/html": new Blob([htmlSignature], { type: "text/html" }),
            "text/plain": new Blob([textSignature], { type: "text/plain" }),
          }),
        ]);

        return true;
      } catch (error) {
        console.warn("Clipboard API falhou. Tentando fallback por selecao.", error);
      }
    }

    return copyHtmlWithSelection(htmlSignature);
  };

  const copyToClipboard = async () => {
    const signatureElement = document.querySelector(".signature");

    if (!(signatureElement instanceof HTMLElement)) {
      setState((prevState) => ({
        ...prevState,
        copyStatus: "error",
      }));
      return;
    }

    const htmlSignature = renderSignatureHtml();
    const textSignature = signatureElement.innerText || signatureElement.textContent || "";

    const copySucceeded = await copyHtmlToClipboard(htmlSignature, textSignature);

    setState((prevState) => ({
      ...prevState,
      copyStatus: copySucceeded ? "success" : "error",
    }));
  };

  // Funcionalidade de baixar a assinatura como arquivo .html — pronta, porem
  // ainda nao ligada a nenhum botao da UI. Mantida de proposito.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const downloadHtmlFile = () => {
    const htmlSignature = renderSignatureHtml();
    const blob = new Blob([htmlSignature], { type: "text/html" });
    const fileDownloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = fileDownloadUrl;
    link.setAttribute("download", buildDownloadFileName(state.fullName));
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(fileDownloadUrl);
  };

  const clearState = () => {
    setState(initialState);
  };

  const openInstructions = () => {
    setIsInstructionsOpen(true);
  };

  const closeInstructions = () => {
    setIsInstructionsOpen(false);
  };

  return (
    <Container>
      <Typography variant="h2" gutterBottom className={classes.centeredText}>
        Gerador de Assinaturas
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Paper className={classes.paper}>
            <form className={classes.root} noValidate autoComplete="off">
              <Grid container spacing={4}>
                <Grid item xs={8}>
                  <TextField
                    id="full-name"
                    fullWidth
                    required
                    label="Nome Completo"
                    value={state.fullName}
                    name="fullName"
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Box className={classes.box}>
                    <FormControl className={classes.inlineSelectControl}>
                      <InputLabel
                        className={classes.inputLabel}
                        id="pronoun-select-label"
                        shrink
                      >
                        Pronome
                      </InputLabel>
                      <Select
                        className={classes.select}
                        labelId="pronoun-select-label"
                        id="pronoun-select"
                        value={state.pronoun}
                        name="pronoun"
                        onChange={handleSelectChange}
                        displayEmpty
                      >
                        <MenuItem value="">
                          Não declarar
                        </MenuItem>
                        {pronounOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <TextField
                    id="title"
                    fullWidth
                    required
                    label="Cargo"
                    value={state.title}
                    name="title"
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="mobile"
                    fullWidth
                    label="Celular"
                    placeholder="(11) 99999-9999"
                    value={state.mobile}
                    name="mobile"
                    onChange={handleChange}
                    inputProps={{ maxLength: 14 }}
                  />
                </Grid>
              </Grid>
              <Box className={classes.box} mt={4}>
                <FormControl fullWidth>
                  <InputLabel className={classes.inputLabel} required id="logo-select">
                    Escolha um Logo
                  </InputLabel>
                  <Select
                    className={classes.select}
                    labelId="logo-select"
                    id="company-select"
                    value={state.companyKey}
                    placeholder="Selecione um Logo"
                    name="companyKey"
                    onChange={handleSelectChange}
                  >
                    {COMPANY_ORDER.map((companyKey) => (
                      <MenuItem key={companyKey} value={companyKey}>
                        {COMPANIES[companyKey].label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Alert
                severity="warning"
                style={{ fontSize: "0.7em", margin: "25px 0 0 0" }}
              >
                O endereço, site e redes sociais seguem a empresa selecionada.
              </Alert>
              <br />
              <Button disabled={!hasFormChanges} onClick={clearState} color="secondary">
                Limpar
              </Button>
            </form>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper className={classes.paper}>
            {state.fullName ? (
              <>
                <Signature {...signatureProps} assetMode="local" />
                <br />
                <Button
                  onClick={copyToClipboard}
                  disabled={!hasRequiredFields}
                  endIcon={
                    state.copyStatus === "success" ? (
                      <CheckOutlined />
                    ) : (
                      <FileCopyOutlined />
                    )
                  }
                >
                  {state.copyStatus === "success" ? "Copiado" : "Copiar"}
                </Button>
                {/*
                <Button
                  endIcon={<DownloadIcon />}
                  onClick={downloadHtmlFile}
                  disabled={!hasRequiredFields}
                >
                  Baixar
                </Button>
                */}
                {state.copyStatus === "success" && (
                  <Alert severity="success" style={{ marginTop: "20px" }}>
                    Siga as instruções para instalar sua assinatura{" "}
                    <Link component="button" type="button" onClick={openInstructions}>
                      clique aqui.
                    </Link>
                  </Alert>
                )}
                {state.copyStatus === "error" && (
                  <Typography variant="body2" color="error" role="alert">
                    Nao foi possivel copiar automaticamente. Tente novamente em Chrome
                    ou Edge, ou use o download do HTML como alternativa.
                  </Typography>
                )}
              </>
            ) : (
              <div>Por favor, preencha seus dados</div>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={isInstructionsOpen}
        onClose={closeInstructions}
        aria-labelledby="instructions-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="instructions-dialog-title">
          Como instalar sua assinatura
        </DialogTitle>
        <DialogContent>
          <video
            className={classes.instructionsVideo}
            controls
            preload="metadata"
            src={mediaAssetUrl("/instrucoes.mp4")}
            aria-label="Vídeo de instruções"
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
}
export default App;